import axios from 'axios'
import { getAccessToken, removeTokens } from '../../storage/auth'
import { runInBackground, markBackground } from '../../shared/background-flag'
import { sessionEndReason } from '../../context/logout-destination'
import { decodeJwtClaims } from '../../shared/jwt'
import { RESTORE_GRACE_MS, isRestrictedSession, restoredAt } from '../../shared/organization-deletion/organization-deletion'
import { carryRequestID, tagRequestID } from '../../shared/error/request-id'

const getConfig = (config, contentType = 'application/json') => {
  config = config || {}

  let headers = {}

  const accessToken = getAccessToken()

  if (accessToken) {
    headers['Authorization'] = accessToken
  }

  headers['Content-Type'] = contentType
  config['headers'] = headers

  return config
}
// A 401 means the credential is no longer good, so END THE SESSION — do not
// merely navigate to the login route.
//
// This used to set window.location and nothing else. The tokens stayed in
// sessionStorage, so the reload came back authenticated: the shell rendered
// with the user's email, every other request still worked, and the login form
// appeared INSIDE the signed-in layout. It looked like a broken auto-logout,
// but no logout had happened.
//
// It also ran during useLogout's own DELETE, which is awaited BEFORE tokens are
// removed. An expired refresh token made that call 401, this navigated, and the
// logout never reached removeTokens — the session outlived the sign-out that
// was trying to end it.
//
// Carrying the destination matters too: useLoginRedirect reads ?redirect as
// base64 (see useBuildRouteRedirect), and without it signing back in always
// started from the landing page instead of the page that was interrupted.
// Pages that answer a 401 themselves: the auth flow (the login screen's own
// requests must not bounce it off itself), and the impersonated tab's
// one-time hand-off, which says the link is no longer valid.
export const skipsAuthRedirect = pathname =>
  pathname.startsWith('/auth') || pathname.startsWith('/redirect/impersonate')

// The organization gate's 401 organization_unavailable (delete
// organization) keeps the session of an owner on the restore page -- the
// restricted restore session is only ever refused off its allowlist, and
// the page must stay (core review RR-B3) -- and of anyone in the minute
// after a restore, while the other services' gates catch up.
export const keepsSessionOnGateRefusal = (error, claims, restored, now = Date.now()) => {
  const response = error && error.response
  if (!response || response.status !== 401 || !response.data || response.data.code !== 'organization_unavailable') return false
  if (isRestrictedSession(claims)) return true
  return !!restored && now - restored < RESTORE_GRACE_MS
}

function checkAuthRedirect(error) {
  if (!error.response || error.response.status !== 401) return

  if (skipsAuthRedirect(window.location.pathname)) return

  if (keepsSessionOnGateRefusal(error, decodeJwtClaims(getAccessToken()), restoredAt())) return

  removeTokens()

  const url = new URL('/auth/email', window.location.origin)
  url.searchParams.set('redirect', btoa(`${window.location.pathname}${window.location.search}`))
  // The server's own reason when it gave one (an impersonation past its
  // 2-hour limit says so), else "expired".
  url.searchParams.set('reason', sessionEndReason(error) || 'expired')

  // replace, not assign: the dead page should not sit in history where Back
  // returns to a shell whose session is gone.
  window.location.replace(url.toString())
}

// The rejection: { error, status }, carrying the failed request's id
// (X-Request-ID) for the error's reference (shared/error/request-id).
function formatError(error) {
  tagRequestID(error)
  if (error.response) {
    if (error.response.data.error) {
      return carryRequestID({ error: error.response.data.error, status: error.response.status }, error)
    } else if (error.response.data) {
      return carryRequestID({ error: error.response.data, status: error.response.status }, error)
    } else {
      return carryRequestID({ error: 'An error occurred.' }, error)
    }
  } else if (error.request) {
    return error.request
  } else {
    return { error: 'An error occurred.' }
  }
}

// runInBackground/markBackground: see ../../shared/background-flag.js (one
// counter shared with shared/api.service.js). Re-exported here so
// use-live-refresh.js's existing `import { runInBackground } from
// '../../api-new/service/api.service'` keeps working unchanged. This is a
// SEPARATE axios wrapper from shared/api.service.js -- use-live-refresh.js's
// sentinel poll and table-ag-infinite.js's chunked data source both import
// getAsync from here, not from shared/api.service.js -- so getAsync here
// still has to call markBackground itself; only the counter is shared.
export { runInBackground }

export const getAsync = (server, endpoint) => {
  return new Promise((resolve, reject) => {
    axios
      .get(markBackground(`${server}/${endpoint}`), getConfig())
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        checkAuthRedirect(error)
        reject(formatError(error))
      })
  })
}

export const deleteAsync = (server, endpoint) => {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${server}/${endpoint}`, getConfig())
      .then(response => {
        resolve(response)
      })
      .catch(error => {
        checkAuthRedirect(error)
        reject(formatError(error))
      })
  })
}
